import { Button, Divider, Typography, useMediaQuery, ChevronDownIcon } from '@ag1/ui';
import { useHeaderContext } from '@portal/state/context/HeaderContext';
import type { Header } from '@portal/types/studio/studio';
import { type SessionCustomer, useLogout } from 'appLayer/auth';
import clsx from 'clsx';
import { routes } from 'constants/routes';
import { useRouter } from 'next/router';
import { useIsShopPageAvailable } from 'processes/ShopV2/hooks/useIsShopPageAvailable';
import { useState } from 'react';
import { useGetNextSubscriptionOrderV2 } from 'state/hooks/useGetNextSubscriptionOrderV2';
import { stripUrlHashAndQuery } from 'state/utils/urlHelpers';
import HeaderMenuLink from '../Navigation/LinkElement/LinkElement';
import styles from './HeaderAccountDropdown.module.scss';
import HeaderOrderStatus from './HeaderOrderStatus';

type DropdownLink = {
  title: string;
  path: string;
  isActivePath?: boolean;
  onClick?: () => void;
};

const DropdownTab = ({ title, path, isActivePath, onClick }: DropdownLink) => (
  <li>
    <HeaderMenuLink href={path} className={styles.accountDropdownTab} onClick={onClick}>
      <span className={isActivePath ? styles.activePath : ''}>{title}</span>
    </HeaderMenuLink>
  </li>
);

const DropdownList = ({
  isOpen = true,
  headerData,
  logoutCallback,
  normalizedPath,
  isShopPageAvailable,
  handleClick
}: {
  isOpen?: boolean;
  headerData: Header | undefined;
  logoutCallback: Function;
  normalizedPath: string;
  isShopPageAvailable: boolean | undefined;
  handleClick?: () => void;
}) => {
  if (!headerData) return null;

  const signOutLink = {
    title: headerData.signOutText || '',
    path: '#',
    onClick: () => logoutCallback()
  };

  const dropdownLinks: DropdownLink[] = [
    ...(headerData.navV2Account?.map((i) => ({
      title: i.text || '',
      path: i.navLink || '',
      isActivePath: i.navLink ? normalizedPath.includes(i.navLink) : false,
      onClick: handleClick
    })) || []),
    signOutLink
  ].filter((i) => !(i.path === routes.shop() && !isShopPageAvailable));

  return (
    <ul className={clsx(styles.accountDropdownListContainer, isOpen && styles.dropdownListOpen)}>
      {dropdownLinks.map(({ title, path, onClick, isActivePath }) => (
        <DropdownTab
          key={title}
          title={title}
          path={path}
          isActivePath={isActivePath}
          onClick={onClick}
        />
      ))}
    </ul>
  );
};

const HeaderAccountDropdown = ({
  show = false,
  handleClick,
  customer
}: {
  show?: boolean;
  handleClick?: () => void;
  customer: SessionCustomer;
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const { isShopPageAvailable } = useIsShopPageAvailable();
  const { nextSubscriptionOrder } = useGetNextSubscriptionOrderV2();
  const { headerData } = useHeaderContext();
  const header = headerData.header || undefined;
  const underM = !useMediaQuery('m');
  const logout = useLogout();
  const router = useRouter();
  const normalizedPath = stripUrlHashAndQuery(router.asPath);

  if (underM) {
    return (
      <div className={styles.accountDropdown}>
        <div className={styles.accountOrderStatusContainer}>
          <Button
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            className={styles.accountOrderStatusNameWrapper}>
            <div className={styles.buttonContent}>
              <Typography
                variant="portalText"
                size="1"
                noPadding
                className={styles.accountOrderStatusName}>
                {header?.customerNamePrefix || ''}
                {customer?.firstName || ''}!
              </Typography>
              <ChevronDownIcon
                className={clsx(
                  styles.orderStatusIcon,
                  isDropdownOpen ? styles.open : styles.closed
                )}
              />
            </div>
          </Button>
          {nextSubscriptionOrder && <HeaderOrderStatus handleClick={handleClick} />}
        </div>
        <DropdownList
          isOpen={isDropdownOpen}
          headerData={header}
          logoutCallback={logout}
          normalizedPath={normalizedPath}
          isShopPageAvailable={isShopPageAvailable}
          handleClick={handleClick}
        />
        <Divider classNameContainer={styles.accountDropdownDivider} />
      </div>
    );
  }

  return (
    <div className={clsx(styles.accountDropdown, show && styles.accountDropdownShow)}>
      {nextSubscriptionOrder && <HeaderOrderStatus handleClick={handleClick} />}

      <DropdownList
        headerData={header}
        logoutCallback={logout}
        normalizedPath={normalizedPath}
        isShopPageAvailable={isShopPageAvailable}
      />
    </div>
  );
};

export default HeaderAccountDropdown;
